import React, {
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from "react";
import { WorkoutContext } from "../../contexts/WorkoutContext";
import { useAuth } from "../../contexts/AuthContext";
import { useUserProfile } from "../../contexts/UserProfileContext";
import {
  BookMarked,
  ChevronRight,
  FolderOpen,
  PenLine,
  Repeat,
  Timer,
  Wand2,
  Activity,
} from "lucide-react";
import TimersList, { useTimerLauncher } from "../workout/TimersList";
import DetailSheet from "./generate/DetailSheet";
import TodayCard from "./TodayCard";
import InstantiateSessionDialog from "../programme/InstantiateSessionDialog";
import useSessionRecipes from "../../hooks/useSessionRecipes";
import useTodaySuggestion from "../../hooks/useTodaySuggestion";
import { requestLogWorkout } from "../../utils/logWorkoutRequest";
import { hasSeenSetup, openSetup } from "../../utils/setupFlow";
import { hasOpenedHelp, openHelp } from "../../utils/helpBus";
import { isLoggedSession } from "../../utils/loggedSession";
import StartChecklist from "./StartChecklist";

const DAY_MS = 24 * 60 * 60 * 1000;

// "Today", "Yesterday", a weekday within the week, else "2 Oct".
const relativeDay = (iso) => {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const startOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const days = Math.round((startOf(new Date()) - startOf(then)) / DAY_MS);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return then.toLocaleDateString("en-AU", { weekday: "long" });
  return then.toLocaleDateString("en-AU", { day: "numeric", month: "short" });
};

/** One row of a "More ways to start" style list. */
const StartRow = ({ icon: Icon, title, subtitle, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="grid w-full grid-cols-[1.25rem_minmax(0,1fr)_1rem] items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-muted/40"
  >
    <Icon className="h-[18px] w-[18px] text-muted-foreground" />
    <span className="min-w-0">
      <span className="block truncate text-sm font-medium text-white">
        {title}
      </span>
      {subtitle && (
        <span className="mt-px block truncate text-xs text-muted-foreground">
          {subtitle}
        </span>
      )}
    </span>
    <ChevronRight className="h-4 w-4 text-muted-foreground/60" />
  </button>
);

/**
 * The Session tab with nothing loaded: one suggestion for today, then the
 * other ways in (build, saved, log, timers) and recent sessions to repeat.
 */
const EmptyState = ({
  onOpenGenerate,
  onOpenGenerateWithRecipe,
  onOpenLoad,
  onStartSuggestion,
  onNavigate,
  startRequest,
  onStartRequestHandled,
}) => {
  const { recipes: sessionRecipes } = useSessionRecipes();
  const { loadSessionFromData } = useContext(WorkoutContext);
  const { isAuthenticated } = useAuth();
  const { sessions } = useUserProfile();
  const {
    programme,
    programmes,
    programmeLoading,
    assignSession,
    programmeSlot,
    isNewUser,
    selector,
    sessionMinutes,
    preview,
    shuffle,
    suggestionOverrides,
    startProgrammeSlot,
  } = useTodaySuggestion();
  const [instantiateOpen, setInstantiateOpen] = useState(false);

  const handleStartToday = useCallback(() => {
    if (programmeSlot) {
      if (!programmeSlot.plan_data) {
        setInstantiateOpen(true);
        return;
      }
      startProgrammeSlot(programmeSlot.plan_data);
      return;
    }
    onStartSuggestion(suggestionOverrides(), { autoGenerate: true });
  }, [programmeSlot, startProgrammeSlot, onStartSuggestion, suggestionOverrides]);

  const [timersOpen, setTimersOpen] = useState(false);
  const timers = useTimerLauncher();
  // Close the Timers sheet before a timer's setup sheet opens over it.
  const timersFromSheet = {
    ...timers,
    openQuick: (mode) => {
      setTimersOpen(false);
      timers.openQuick(mode);
    },
    openSaved: (custom) => {
      setTimersOpen(false);
      timers.openSaved(custom);
    },
  };

  // "Build my first session" at the end of getting started: build today's
  // suggestion as soon as it's known (after any programme has loaded).
  // Handed over from the Session view (see startRequest there).
  useEffect(() => {
    if (!startRequest) return;
    if (isAuthenticated && programmeLoading) return;
    if (!programmeSlot && !preview?.archetype) return;
    onStartRequestHandled?.();
    if (startRequest.action === "customise") onStartSuggestion(suggestionOverrides());
    else handleStartToday();
  }, [
    startRequest,
    onStartRequestHandled,
    isAuthenticated,
    programmeLoading,
    programmeSlot,
    preview,
    handleStartToday,
    onStartSuggestion,
    suggestionOverrides,
  ]);

  // Repeat a recent session: the two most recent with a plan to rerun.
  const recentSessions = useMemo(() => {
    let source = sessions;
    if (!source?.length) {
      try {
        source = JSON.parse(localStorage.getItem("completedSessions") || "[]");
      } catch {
        source = [];
      }
    }
    return (Array.isArray(source) ? source : [])
      .filter((s) => s?.plan_data)
      .slice(0, 2)
      .map((s) => ({
        id: s.id,
        label: s.plan_data?.label || s.workout_type || "Workout",
        meta: [
          relativeDay(s.created_at),
          s.duration_minutes ? `${s.duration_minutes} min` : null,
        ]
          .filter(Boolean)
          .join(" · "),
        planData: s.plan_data,
      }));
  }, [sessions]);

  const now = new Date();
  const dateLine = now.toLocaleDateString("en-AU", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  const greeting = !sessions?.length && recentSessions.length === 0
    ? "Welcome to Khronos"
    : now.getHours() < 12
      ? "Good morning"
      : now.getHours() < 17
        ? "Good afternoon"
        : "Good evening";

  // Ticks itself off from what's actually happened.
  const allSessions = isAuthenticated && sessions?.length ? sessions : (() => {
    try {
      return JSON.parse(localStorage.getItem("completedSessions") || "[]");
    } catch {
      return [];
    }
  })();
  const checklist = [
    {
      id: "setup",
      title: "Set up Khronos",
      hint: "Four quick questions about your goal, time and kit",
      done: hasSeenSetup(),
      onClick: openSetup,
    },
    {
      id: "first",
      title: "Do your first session",
      hint: "Start today's suggestion above",
      done: allSessions.some((s) => !isLoggedSession(s)),
      onClick: handleStartToday,
    },
    {
      id: "log",
      title: "Log a workout you did elsewhere",
      hint: "A run, a class, or a Garmin import",
      done: allSessions.some((s) => isLoggedSession(s)),
      onClick: () => {
        requestLogWorkout();
        onNavigate?.("activity");
      },
    },
    {
      id: "programme",
      title: "Try a programme",
      hint: "A plan over several weeks towards one goal",
      done: Boolean(programme) || (programmes?.length || 0) > 0,
      onClick: () => onNavigate?.("programme"),
    },
    {
      id: "help",
      title: "Find your way around",
      hint: "Short guides to every part of Khronos",
      done: hasOpenedHelp(),
      onClick: () => openHelp(),
    },
  ];



  return (
    <div className="mx-auto grid max-w-5xl grid-cols-[minmax(0,1fr)] gap-5 pb-8 pt-4 md:pt-8 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] lg:items-start lg:gap-x-8">
      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] content-start gap-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            {dateLine}
          </p>
          <h2 className="mt-0.5 text-2xl font-bold text-white">{greeting}</h2>
        </div>

        {programmeLoading && isAuthenticated ? (
          <div
            aria-hidden="true"
            className="h-[13.5rem] animate-pulse rounded-2xl border border-border bg-muted/30"
          />
        ) : (
          <div data-tour="today">
          <TodayCard
            programme={programme}
            programmeSlot={programmeSlot}
            selector={selector}
            preview={preview}
            sessionMinutes={sessionMinutes}
            isNewUser={isNewUser}
            onStart={handleStartToday}
            onShuffle={shuffle}
            onCustomise={() => onStartSuggestion(suggestionOverrides())}
            onOpenProgramme={() => onNavigate?.("programme")}
          />
          </div>
        )}

        <StartChecklist items={checklist} />


        {/* Session recipes (issue #78) — the fastest route to a generated
            session, since a recipe already answers the form's questions. */}
        {sessionRecipes.length > 0 && (
          <div className="grid gap-2">
            <h4 className="flex items-baseline justify-between text-xs font-semibold text-muted-foreground">
              Your recipes
              <span className="font-normal text-muted-foreground/70">
                one tap to fill the form
              </span>
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {sessionRecipes.slice(0, 6).map((recipe) => (
                <button
                  key={recipe.id}
                  type="button"
                  onClick={() => onOpenGenerateWithRecipe(recipe.id)}
                  className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-card px-3.5 py-2 text-[13px] text-foreground hover:border-primary/50"
                >
                  <BookMarked className="h-3.5 w-3.5 shrink-0 text-primary" />
                  <span className="truncate">{recipe.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] content-start gap-5">
        <div className="grid gap-2" data-tour="more-ways">
          <h4 className="text-xs font-semibold text-muted-foreground">
            More ways to start
          </h4>
          <div className="overflow-hidden rounded-2xl border border-border bg-card divide-y divide-border">
            {/* Free training (#146, #163): for when you just want to start. */}
            <StartRow
              icon={Activity}
              title="Just train"
              subtitle="Free training: your plan or none. Log what you do as you go"
              onClick={() => timers.openQuick("free")}
            />
            <StartRow
              icon={Wand2}
              title="Build your own"
              subtitle="Choose goals, time and kit"
              onClick={onOpenGenerate}
            />
            <StartRow
              icon={FolderOpen}
              title="Saved sessions"
              subtitle="Your library and shared sessions"
              onClick={onOpenLoad}
            />
            {isAuthenticated && (
              <StartRow
                icon={PenLine}
                title="Log a workout you've done"
                subtitle="A run, a class, anything off the app"
                onClick={() => {
                  requestLogWorkout();
                  onNavigate?.("activity");
                }}
              />
            )}
            <StartRow
              icon={Timer}
              title="Timers"
              subtitle="Tabata, EMOM, stopwatch and more"
              onClick={() => setTimersOpen(true)}
            />
          </div>
        </div>

        {recentSessions.length > 0 && (
          <div className="grid gap-2">
            <h4 className="text-xs font-semibold text-muted-foreground">
              Repeat a recent session
            </h4>
            <div className="overflow-hidden rounded-2xl border border-border bg-card divide-y divide-border">
              {recentSessions.map((session) => (
                <StartRow
                  key={session.id || session.label}
                  icon={Repeat}
                  title={session.label}
                  subtitle={session.meta}
                  onClick={() => loadSessionFromData(session.planData)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <DetailSheet
        open={timersOpen}
        onOpenChange={setTimersOpen}
        title="Timers"
        description="Start a timer without a planned session"
      >
        <TimersList launcher={timersFromSheet} />
      </DetailSheet>

      {timers.sheet}

      {programmeSlot && (
        <InstantiateSessionDialog
          open={instantiateOpen}
          onOpenChange={setInstantiateOpen}
          targetSession={programmeSlot}
          programme={programme}
          onAssignSession={assignSession}
          mode="generate"
          autoGenerate
          onAssigned={startProgrammeSlot}
        />
      )}
    </div>
  );
};

export default EmptyState;
